import { useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSession } from "@/lib/auth-client";
import { Loader2, Mail, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/verify-pending")({
  beforeLoad: async ({ context }) => {
    const session = context.session;

    if (session?.user?.emailVerified) {
      throw redirect({
        to: "/dashboard",
      });
    }
  },
  component: VerifyPendingPage,
  validateSearch: (search: Record<string, unknown>): { email?: string } => {
    return {
      email: search.email as string | undefined,
    };
  },
});

const RESEND_COOLDOWN = 60;

function VerifyPendingPage() {
  const search = Route.useSearch();
  const { data: session, refetch } = useSession();
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const email = search.email || session?.user?.email || "";

  useEffect(() => {
    if (session?.user?.emailVerified) {
      window.location.href = "/dashboard";
    }
  }, [session]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 3000);

    return () => clearInterval(interval);
  }, [refetch]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;

    setIsResending(true);
    setResendError(null);

    try {
      const response = await fetch("/api/auth/send-verification-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error("Failed to send verification email");
      }

      setCooldown(RESEND_COOLDOWN);
    } catch {
      setResendError("Failed to send verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const maskEmail = (email: string) => {
    const [localPart, domain] = email.split("@");
    if (!domain) return email;

    const visibleChars = Math.min(3, Math.floor(localPart.length / 2));
    const masked = localPart.slice(0, visibleChars) + "***";
    return `${masked}@${domain}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <div className="mx-auto size-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Mail className="size-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">Check your email</h1>
          <p className="text-muted-foreground">
            We've sent a verification link to
          </p>
          {email && (
            <p className="font-medium text-foreground">{maskEmail(email)}</p>
          )}
        </div>

        <Card className="border-border rounded-3xl shadow-xl">
          <CardContent className="pt-8 pb-8 px-8 text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Click the link in the email to verify your account. The link will
              expire in 1 hour.
            </p>

            <div className="space-y-2">
              <Button
                onClick={handleResend}
                disabled={cooldown > 0 || isResending}
                variant="outline"
                className="w-full"
              >
                {isResending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Sending...
                  </>
                ) : cooldown > 0 ? (
                  <>
                    <RefreshCw className="size-4" />
                    Resend in {cooldown}s
                  </>
                ) : (
                  <>
                    <RefreshCw className="size-4" />
                    Resend verification email
                  </>
                )}
              </Button>

              {resendError && (
                <p className="text-xs text-destructive">{resendError}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Already verified?{" "}
          <a href="/login" className="font-medium hover:underline">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
